import React, { useCallback, useEffect, useState, forwardRef } from "react";
import { Divider, makeStyles } from "@fluentui/react-components";

import FluentChatOverlay from "./FluentChatOverlay";
import FluentChatPublic from "./FluentChatPublic";
import { usePermissions } from "../room/usePermissions";
import FluentChatPrivate from "./FluentChatPrivate";
import FluentChatAI from "./FluentChatAI";
import { VADProvider } from "../chat/AIChatContext";
import useBreakpoints from "../hooks/useBreakpoints";
import PropTypes from "prop-types";
import { useMicrophoneStatus } from "../room/useMicrophoneStatus";
import { ResponsiveOverlayDrawer } from "../drawer/ResponsiveOverlayDrawer";
// import { VoskMicrophoneProvider } from "../room/hooks/vosk/useMicrophoneVosk";

const useStyles = makeStyles({
  halfOverlay: {
    top: "50%!important",
    boxShadow: "none",
    animation: "none",
    borderTop: "var(--color-border-default)"
  },
  drawer: {
    width: "400px",
    "& > *": {
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar-button": {
        display: "none"
      },
      "&::-webkit-scrollbar-button:vertical:decrement, &::-webkit-scrollbar-button:vertical:increment": {
        display: "none", // Specifically hide the up/down buttons
        height: "0" // Ensure no space is left for the button
      }
    }
  },
  headerDrawer: {
    display: " flex",
    // padding:" var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    // alignItems:" flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "var(--primaryColorBackground)"
  },
  drawerBody: {
    paddingTop: "20px"
  },
  search: {
    backgroundColor: "var(--secondaryColorBackground)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--color-text-secondary)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "var(--accent1-color-hover)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  contentParticipants: {},
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "var(--primaryColorBackground)"
  },
  redIcon: {
    color: "var(--color-danger)"
  },
  messageListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px"
  },
  messageBubble: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "5px"
  },
  messageBubbleUser: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "5px"
  },
  messageAuthorContainer: {
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "10px",
    display: "inline-flex"
  },
  messageAuthorContainerOther: {
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "10px",
    display: "inline-flex"
  },
  messageAuthorDate: {
    textAlign: "right",
    color: "var(--color-text-secondary)",
    fontSize: 14,
    fontFamily: "Segoe UI",
    fontWeight: "400"
  },
  messageAuthor: { textAlign: "right", color: "white", fontSize: 14, fontFamily: "Segoe UI", fontWeight: "700" },
  messageBubbleContent: {
    display: "flex",
    padding: "20px 15px",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    alignSelf: "stretch",
    borderRadius: "20px 0px 20px 20px",
    background: "var(--secondaryColorBackground)",
    maxWidth: "280px",
    marginLeft: "auto"
  },
  messageBubbleContentOther: {
    display: "flex",
    padding: "20px 15px",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    alignSelf: "stretch",
    borderRadius: "0px 20px 20px 20px",
    background: "var(--secondaryColorBackground)",
    maxWidth: "280px"
  },
  messageBubbleText: {
    color: "var(--color-text-primary)",
    textAlign: "right"
  },
  sendStyles: {
    minWidth: "32px",
    width: "32px"
  },
  sendMessageContainer: {
    display: "flex",
    gap: "8px",
    width: "100%",
    justifyContent: "space-between"
  }
});

export default function FluentChatOverlayContainer({
  isOpenChat,
  isOpenParticipants,
  selectedUser,
  scene,
  closeChat,
  canSendChat,
  ...rest
}) {
  const styles = useStyles();
  const mountSocial = document.querySelector("#mount-chat");

  const { text_chat: canTextChat } = usePermissions();

  const { isMicMuted, toggleMute } = useMicrophoneStatus(scene);

  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === "sm" || breakpoint === "xs";

  const overlayStyle = isMobile ? {} : { width: "400px" };

  const [isOpen, setIsOpen] = useState(isOpenChat);

  useEffect(() => {
    setIsOpen(isOpenChat);
  }, [isOpenChat, mountSocial, selectedUser]);

  const handleOpenChange = useCallback(
    (_, { open }) => {
      setIsOpen(open);

      if (!open) {
        closeChat();
      }
    },
    [closeChat]
  );

  return (
    <VADProvider entered micActiveAI={!isMicMuted} scene={scene}>
      <ResponsiveOverlayDrawer
        size="full"
        desktopPosition="start"
        mobilePosition={isOpenParticipants ? "start" : "bottom"}
        surfaceMotion={isOpenParticipants ? null : undefined}
        // mountNode={mountSocial}
        modalType="non-modal"
        open={isOpen}
        onOpenChange={handleOpenChange}
        separator
        className={isOpenParticipants ? styles.halfOverlay : styles.overlayFull}
        style={isOpenParticipants ? { ...overlayStyle } : overlayStyle}
      >
        {isOpenParticipants && <Divider style={{ flexGrow: 0, height: "1px" }} />}
        {!selectedUser && (
          <FluentChatPublic
            canTextChat={canTextChat}
            canSendChat={canSendChat}
            selectedUser={selectedUser}
            toggleChat={closeChat}
            {...rest}
          />
        )}
        {selectedUser && !selectedUser.isAi && (
          <FluentChatPrivate
            canTextChat={canTextChat}
            canSendChat={canSendChat}
            selectedUser={selectedUser}
            toggleChat={closeChat}
            {...rest}
          />
        )}
        {selectedUser && selectedUser.isAi && (
          <FluentChatAI
            micActive={!isMicMuted}
            setMicActive={toggleMute}
            canTextChat={canTextChat}
            canSendChat={canSendChat}
            selectedUser={selectedUser}
            toggleChat={closeChat}
            scene={scene}
            isOpenParticipants={isOpenParticipants}
            {...rest}
          />
        )}
      </ResponsiveOverlayDrawer>
    </VADProvider>
  );
}

FluentChatOverlayContainer.propTypes = {
  canSendChat: PropTypes.bool,
  closeChat: PropTypes.func.isRequired,
  isOpenChat: PropTypes.bool.isRequired,
  isOpenParticipants: PropTypes.bool,
  scene: PropTypes.object.isRequired,
  selectedUser: PropTypes.shape({
    isAi: PropTypes.bool
  })
};

export const ChatMessageList = forwardRef(({ children, ...rest }, ref) => (
  <ul {...rest} ref={ref}>
    {children}
  </ul>
));

ChatMessageList.displayName = "ChatMessageList";

ChatMessageList.propTypes = {
  children: PropTypes.node.isRequired
};

FluentChatOverlay.propTypes = {
  hubChannel: PropTypes.object.isRequired,
  toggleChat: PropTypes.func.isRequired,
  selectedUser: PropTypes.object,
  messageGroups: PropTypes.array.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onUploadAttachments: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  setMessage: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isCommand: PropTypes.bool.isRequired,
  onSpawn: PropTypes.func.isRequired,
  inputRef: PropTypes.object,
  onSelectEmoji: PropTypes.func.isRequired,
  canTextChat: PropTypes.bool.isRequired
};
